import React, { useState, useEffect, useRef } from 'react';
import { Trash2, Repeat, Tag, X, Filter } from 'lucide-react';
import { Button, Input, ICON_PX } from '../ui';
import ConditionEditor, { summarizeCondition } from './ConditionEditor';

/**
 * Floating popover for editing an edge — set label, condition, mark as loop, delete.
 * Positions itself near the click point on the canvas.
 */
function EdgeConfigPopover({ edge, position, onUpdateEdge, onDeleteEdge, onClose }) {
  const [label, setLabel] = useState(edge?.label || '');
  const [showLabelInput, setShowLabelInput] = useState(false);
  const [showConditionEditor, setShowConditionEditor] = useState(false);
  const popoverRef = useRef(null);

  useEffect(() => {
    setLabel(edge?.label || '');
    setShowLabelInput(Boolean(edge?.label));
    setShowConditionEditor(false);
  }, [edge]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  if (!edge || !position) return null;

  const handleSaveLabel = () => {
    onUpdateEdge?.(edge.id, { label });
    setShowLabelInput(false);
  };

  const handleToggleLoop = () => {
    onUpdateEdge?.(edge.id, { isLoop: !edge.isLoop });
  };

  const handleConditionChange = (condition) => {
    onUpdateEdge?.(edge.id, { guard: condition, label: `if ${summarizeCondition(condition)}` });
    setShowConditionEditor(false);
  };

  const handleRemoveCondition = () => {
    onUpdateEdge?.(edge.id, { guard: undefined, label: '' });
    setShowConditionEditor(false);
  };

  return (
    <div
      ref={popoverRef}
      className="fixed z-50 bg-white border border-slate-200 rounded-xl shadow-lg p-2 min-w-[220px] max-w-[300px]"
      style={{ left: position.x, top: position.y }}
    >
      <div className="flex items-center justify-between px-2 py-1 mb-1 border-b border-slate-100">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Connection</span>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-text-dark p-0.5">
          <X size={ICON_PX.sm} />
        </button>
      </div>

      {/* Label editor */}
      {showLabelInput ? (
        <div className="px-2 py-1.5 space-y-1.5">
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveLabel(); }}
            placeholder="e.g., If yes, If failed"
            autoFocus
            className="text-sm"
          />
          <div className="flex gap-1.5">
            <Button variant="ghost" size="sm" onClick={() => { setShowLabelInput(false); setLabel(edge.label || ''); }}>
              Cancel
            </Button>
            <Button variant="save" size="sm" onClick={handleSaveLabel}>Save</Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowLabelInput(true)}
          className="w-full text-left px-2 py-2 hover:bg-slate-50 rounded-lg flex items-center gap-2 text-sm text-text-dark"
        >
          <Tag size={ICON_PX.sm} className="text-slate-400" />
          {edge.label ? `Edit label: "${edge.label}"` : 'Add a label'}
        </button>
      )}

      {/* Condition editor */}
      {showConditionEditor ? (
        <div className="px-1 py-1">
          <ConditionEditor
            condition={edge.guard}
            onChange={handleConditionChange}
            onRemove={handleRemoveCondition}
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowConditionEditor(true)}
          className={`w-full text-left px-2 py-2 hover:bg-slate-50 rounded-lg flex items-center gap-2 text-sm ${edge.guard ? 'text-[var(--yellow)]' : 'text-text-dark'}`}
        >
          <Filter size={ICON_PX.sm} className={edge.guard ? 'text-[var(--yellow)]' : 'text-slate-400'} />
          {edge.guard ? `Condition: ${summarizeCondition(edge.guard)}` : 'Add a condition'}
        </button>
      )}

      {/* Loop toggle */}
      <button
        type="button"
        onClick={handleToggleLoop}
        className={`w-full text-left px-2 py-2 hover:bg-slate-50 rounded-lg flex items-center gap-2 text-sm ${edge.isLoop ? 'text-aqua' : 'text-text-dark'}`}
      >
        <Repeat size={ICON_PX.sm} className={edge.isLoop ? 'text-aqua' : 'text-slate-400'} />
        {edge.isLoop ? 'Unmark as loop' : 'Mark as loop'}
      </button>

      {/* Delete */}
      <button
        type="button"
        onClick={() => { onDeleteEdge?.(edge.id); onClose?.(); }}
        className="w-full text-left px-2 py-2 hover:bg-red-bg rounded-lg flex items-center gap-2 text-sm text-red-critical"
      >
        <Trash2 size={ICON_PX.sm} />
        Delete connection
      </button>
    </div>
  );
}

export default EdgeConfigPopover;
